import { CircularProgress, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  accountsApi,
  useFindExternalDataForAccountsMutation,
  useGetSuggestTitlesMutation,
} from "../../../../api";
import { JobTitle } from "../../../../types/apiTypes";
import { getErrorMessage } from "../../../../utils/getErrorMessage";
import { JobTitlesSortableList } from "../../../features/JobTitlesSortableList";
import { CustomToast } from "../../CustomToast";
import { ReuseModal } from "../../ReuseModal";
import { JobTitlesListHeader } from "../GetMoreContactsModal/JobTitleListHeader";
import { useAppDispatch } from "../../../../hooks";
import { ReuseTooltip } from "../../ReuseTooltip";
import { CustomButton } from "../../CustomButton";
import { StarsOutlineIcon } from "../../../images";

type Props = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  initTitles: JobTitle[];
  campaign_id: string;
  onSuccess: () => void;
  selectedAccounts: string[];
};

export const EditJobTitlesModal = ({
  isOpen,
  setIsOpen,
  initTitles,
  campaign_id,
  onSuccess,
  selectedAccounts,
}: Props) => {
  const dispatch = useAppDispatch();
  const [titles, setTitles] = useState<JobTitle[]>(initTitles);
  const [createdTitleId, setCreatedTitleId] = useState<string>("");
  const [searchTitleId, setSearchTitleId] = useState<string>("");
  const [searchValue, setSearchValue] = useState<string>("");
  const [searchTitles, setSearchTitles] = useState<string[]>([]);
  const [isNoSuggestions, setIsNoSuggestions] = useState<boolean>(false);

  const [getSuggestTitles, { isLoading: isSuggestLoading }] =
    useGetSuggestTitlesMutation();

  const [findExternalDataForAccounts, { isLoading, error, isError }] =
    useFindExternalDataForAccountsMutation();

  useEffect(() => {
    if (isError && error) {
      toast.error(
        <CustomToast
          type="error"
          title="Failed to find external data for accounts."
          text={
            getErrorMessage(error).globalError ||
            "Something went wrong. Please try again"
          }
        />,
      );
    }
  }, [isError, error]);

  useEffect(() => {
    setTitles(initTitles);
  }, [initTitles]);

  useEffect(() => {
    const searchTitleList = titles
      .filter(({ title }) =>
        title.toLowerCase().includes(searchValue.toLowerCase()),
      )
      .map(({ id }) => id);
    setSearchTitles(searchTitleList);
    setSearchTitleId(searchValue ? searchTitleList[0] : "");
  }, [searchValue, titles]);

  const addTitleHandler = (title: string, successHandler?: () => void) => {
    const id = Date.now().toString();
    if (
      titles
        .filter((el) => el.id !== id)
        .some(
          (el) => el.title.trim().toLowerCase() === title.trim().toLowerCase(),
        )
    ) {
      toast.error(
        <CustomToast
          type="error"
          title="Job Title Update Failed"
          text="Each job title must be unique. Please choose a different title."
        />,
      );
      return;
    }

    setTitles([...titles, { id, title, is_critical: true, is_edit: false }]);
    setCreatedTitleId(id);

    successHandler && successHandler();
  };

  const submitHandler = () => {
    if (titles.some(({ is_edit, is_suggest }) => is_edit && !is_suggest)) {
      toast.error(
        <CustomToast
          type="error"
          title="Invalid Job Title"
          text="Please complete editing before proceeding."
        />,
      );
      return;
    }

    findExternalDataForAccounts({
      account_ids: selectedAccounts,
      campaign_id,
      title_gaps: titles.map(({ title, is_critical }) => ({
        name: title,
        is_critical,
      })),
    }).then(() => {
      onSuccess();
      setIsOpen(false);
      dispatch(accountsApi.util.invalidateTags(["list-view"]));
    });
  };

  const isNoTitles = titles.length === 0;

  const setSearchElIndexHandler = (index: number) => {
    setSearchTitleId(searchTitles[index]);
  };

  const addSuggests = () => {
    const existingTitles = titles.filter(({ is_suggest }) => !is_suggest);

    getSuggestTitles({
      existing_titles: existingTitles.map(({ title }) => title),
      account_id: selectedAccounts[0],
    })
      .unwrap()
      .then(({ suggestions }) => {
        if (suggestions.length === 0) {
          setIsNoSuggestions(true);
          return;
        }
        const preperTitles = suggestions.map((title) => ({
          id: title,
          title,
          is_critical: true,
          is_edit: true,
          is_suggest: true,
        }));

        setTitles([...existingTitles, ...preperTitles]);
        setCreatedTitleId(suggestions[suggestions.length - 1]);
      })
      .catch((err) => {
        toast.error(
          <CustomToast
            type="error"
            title="Failed to find more suggestions."
            text={
              getErrorMessage(err).globalError ||
              "Something went wrong. Please try again"
            }
          />,
        );
        setIsNoSuggestions(true);
      });
  };

  return (
    <ReuseModal
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      title="Explore INFUSE Audience Expansion"
      maxWidth="548px"
      submitBtnText="Expand Audience"
      cancelBtnText="Back"
      submitBtnColor="#1C3C6C"
      onSubmit={submitHandler}
      isLoading={isLoading}
      isDisabled={isLoading || titles.length === 0}
    >
      <Stack gap="16px">
        <Stack
          flexDirection="row"
          gap="16px"
          alignItems="flex-end"
          justifyContent="space-between"
          width="100%"
        >
          <Typography component="span" variant="body1" color="text.secondary">
            Customize the list of titles for audience expansion.
          </Typography>
          <ReuseTooltip
            text={
              isNoSuggestions
                ? "There are currently no new job titles to suggest"
                : ""
            }
            placement="top"
            hideChildren={false}
          >
            <CustomButton
              onClick={addSuggests}
              variant="outlined"
              customColor="#FFFFFF"
              textColor="#BD4D00"
              customSize="fit"
              type="button"
              startIcon={
                isSuggestLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  !isNoSuggestions && <StarsOutlineIcon />
                )
              }
              disabled={isNoSuggestions || isSuggestLoading}
              sx={{
                height: "42px",
                borderColor: "#BD4D00",
                minWidth: "170px",
                maxWidth: "170px",
              }}
            >
              {isNoSuggestions ? "No Suggestions" : "Suggest Titles"}
            </CustomButton>
          </ReuseTooltip>
        </Stack>
        <JobTitlesListHeader
          addTitleHandler={addTitleHandler}
          setSearchValue={setSearchValue}
          searchTotalCount={searchTitles.length}
          searchElIndex={searchTitles.findIndex((el) => searchTitleId === el)}
          setSearchElIndex={setSearchElIndexHandler}
          isLimit={titles.length >= 100}
        />
        {!isNoTitles && (
          <JobTitlesSortableList
            titles={titles}
            setTitles={setTitles}
            createdTitleId={createdTitleId}
            setCreatedTitleId={setCreatedTitleId}
            searchTitleId={searchTitleId}
          />
        )}
      </Stack>
    </ReuseModal>
  );
};
